import { useMediaQuery } from "@mui/material";
import { useLocation } from "@reach/router";
import { useContext, useEffect, useMemo, useState } from "react";
import { boot, show } from "@intercom/messenger-js-sdk";
import { NOT_DASHBOARD_ROUTES } from "../commons/enum";
import calculateSummaryData from "../components/Layout/calculate-summary-data";
import CheckUserLoggedInContext from "../context/check-user-logged-in-context";
import DataLoadingContext from "../context/data-loading-context";
import { useIntercomToken, useLinkedBankAccountData } from "./account.hooks";
import { usePropertyList } from "./property.hooks";
import { useLastMonthFinanceHistory } from "./scenario.hooks";
import { useIsLoggedIn, useMyInfo } from "./user.hooks.ts";

export const useIsInDashboardRoutes = (pathname) => {
  return useMemo(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const route of NOT_DASHBOARD_ROUTES) {
      if (pathname.indexOf(route) >= 0) {
        return false;
      }
    }
    return true;
  }, [pathname]);
};

export const useShouldRedirectWhenAuthenticated = () => {
  const isLoggedIn = useIsLoggedIn();
  const location = useLocation();
  const { shouldPauseAuthenticatedRedirect } = useContext(DataLoadingContext);
  const { checkUserLoggedIn } = useContext(CheckUserLoggedInContext);
  const isInDashboardRoutes = useIsInDashboardRoutes(location.pathname);
  return useMemo(() => {
    if (
      isLoggedIn &&
      !isInDashboardRoutes &&
      !shouldPauseAuthenticatedRedirect
    ) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    checkUserLoggedIn,
    isInDashboardRoutes,
    shouldPauseAuthenticatedRedirect,
  ]);
};

export const useFinanceSummary = () => {
  const linkedBankAccountData = useLinkedBankAccountData();
  const { data: propertyList } = usePropertyList();
  const { data: financeData } = useLastMonthFinanceHistory();

  return useMemo(() => {
    return calculateSummaryData(
      linkedBankAccountData,
      propertyList,
      financeData
    );
  }, [linkedBankAccountData, propertyList, financeData]);
};

export const useStartLiveChat = () => {
  const { data: userInfo } = useMyInfo();
  const { data: intercomToken } = useIntercomToken();
  const {
    firstName = "Anonymous",
    lastName = "",
    id = "",
    email = "",
  } = userInfo;
  const fullName = `${firstName} ${lastName}`;
  const user = {
    app_id: intercomToken?.appId || "",
    user_id: id,
    email,
    name: fullName,
  };

  const startLiveChat = () => {
    boot(user);
    show();
  };

  return [startLiveChat];
};

export const useIsDesktopSmall = () => {
  return useMediaQuery("(max-width:1600px)");
};

export const useIsScreenMd = () => {
  return useMediaQuery("(max-width: 1024px)");
};

export const useIsScreenSm = () => {
  return useMediaQuery("(max-width: 599px)");
};

export const useIsTablet = () => {
  return useMediaQuery("(max-width: 1279px)");
};

export const useIsMobile = () => {
  return useMediaQuery("(max-width: 767px)");
};

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
};

export const useDebouncedFunc = (func, delay = 300) => {
  const [id, setId] = useState(null);

  return (...args) => {
    if (id) {
      clearTimeout(id);
    }
    setId(
      setTimeout(() => {
        func(...args);
        setId(null);
      }, delay)
    );
  };
};
